import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
  HostListener,
} from '@angular/core';
import { CalendarModalComponent } from '../calendar-modal/calendar-modal.component';
import { ToastrService } from 'ngx-toastr';
import { InspectionService } from '../../../inspection.service';
import { StatusOption } from '../../flag-manager.interfaces';
import { FlagManagerService } from '../../flag-manager.service';
import { UnderwritingStatus } from '../../../../shared/interfaces';

@Component({
  selector: 'app-flag-manger-header',
  templateUrl: './flag-manger-header.component.html',
  styleUrls: ['./flag-manger-header.component.scss'],
})
export class FlagMangerHeaderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() inspection!: any;
  @Input() statuses: StatusOption[] = [];
  @Input() showReportButton = false;
  @Input() underwritingStatus: number;

  @Output() readonly statusChange = new EventEmitter<number>();
  @Output() readonly flagRefresh = new EventEmitter<number>();
  @Output() readonly openLetterHistory = new EventEmitter<void>();
  @Output() readonly refreshInspection = new EventEmitter<any>();
  @ViewChild(CalendarModalComponent) calendarModalComponent!: CalendarModalComponent;

  selectedStatusId = 0;
  selectedDateType?: string;
  selectedDate?: Date;
  showGenerateLetterModal = false;
  isDropdownOpen = false;
  customerUrl: string;
  isPreviewLetterOpen = false;
  showJournalPanel = false;

  constructor(
    private readonly inspectionService: InspectionService,
    private readonly toastr: ToastrService,
    private readonly flagManagerService: FlagManagerService,
  ) {}

  ngOnInit(): void {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inspection'] && this.inspection) {
      console.log('inspection', this.inspection);
      this.selectedStatusId = this.inspection.underwritingStatus;
    }
    if (changes['underwritingStatus'] && this.underwritingStatus) {
      this.selectedStatusId = this.underwritingStatus;
    }
  }

  get selectedStatus(): StatusOption {
    return (
      this.statuses.find(status => status.id === this.selectedStatusId) ?? {
        id: this.selectedStatusId,
        label: 'Select Option',
        colorClass: 'gray',
      }
    );
  }

  async updateUnderwritingStatus(id: number): Promise<void> {
    this.selectedStatusId = id;
    if (this.inspection) {
      try {
        await this.flagManagerService.updateUnderwritingStatus(this.inspection.id, this.selectedStatusId || -1);
        this.statusChange.emit(this.selectedStatusId);
      } catch (error) {
        this.selectedStatusId = this.inspection.underwritingStatus;
      }
    }
  }

  openCalendarModal(type: string, date: Date): void {
    this.selectedDateType = type;
    this.selectedDate = date;
    setTimeout(() => {
      this.calendarModalComponent.openModal();
    });
  }

  async onSave(selectedDate: Date): Promise<void> {
    if (!selectedDate) return;

    const newDeadlines = {
      violationRecommendedDeadline: this.inspection.violationRecommendedDeadline,
      violationActionRequiredDeadline: this.inspection.violationActionRequiredDeadline,
    };

    const deadlineKey =
      this.selectedDateType === 'Immediate Recs Due'
        ? 'violationActionRequiredDeadline'
        : 'violationRecommendedDeadline';

    const isFirstTimeAdd = !this.inspection[deadlineKey];

    newDeadlines[deadlineKey] = selectedDate;

    this.inspection = await this.inspectionService.setDeadlines(this.inspection.id, newDeadlines);
    this.refreshInspection.emit(this.inspection);
    if (this.inspection.underwritingStatus === UnderwritingStatus.PREPARE_FLAGS) {
      this.flagRefresh.emit(UnderwritingStatus.PREPARATION_IN_PROGRESS);
    } else {
      this.flagRefresh.emit();
    }

    if (deadlineKey === 'violationActionRequiredDeadline') {
      this.toastr.success(
        isFirstTimeAdd
          ? 'You have successfully added the immediate rec due date.'
          : 'You have successfully changed the immediate rec due date.',
      );
    } else if (deadlineKey === 'violationRecommendedDeadline') {
      this.toastr.success(
        isFirstTimeAdd
          ? 'You have successfully added the other rec due date.'
          : 'You have successfully changed the other rec due date.',
      );
    }
  }

  toggleDropdown(): void {
    this.isDropdownOpen = !this.isDropdownOpen;
  }

  openGenerateLetterModal(preview: boolean = false): void {
    this.isDropdownOpen = false;
    this.isPreviewLetterOpen = preview;
    this.showGenerateLetterModal = true;
  }

  openPreviewLetterModal(): void {
    this.openGenerateLetterModal(true);
  }

  closeGenerateLetterModal(): void {
    this.showGenerateLetterModal = false;
    this.isPreviewLetterOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const dropdownElement = target.closest('.generate-report-dropdown');

    if (!dropdownElement && this.isDropdownOpen) {
      this.isDropdownOpen = false;
    }
  }

  async handleCreateLetter(event: any): Promise<void> {
    const { userId, createVersionFlag } = event;
    const recLetterMessage =
      this.inspection.underwritingStatus >= UnderwritingStatus.LETTER_SENT
        ? 'You have successfully created a rec letter.'
        : 'You have successfully created a rec letter. All flags are placed into the Flags for resolve tab.';
    this.toastr.success(recLetterMessage);

    try {
      let newLetter;
      if (createVersionFlag) {
        newLetter = await this.inspectionService.generateLetter(this.inspection.id, userId);
      } else {
        newLetter = await this.inspectionService.generateNoIssuesLetter(this.inspection.id, userId);
        newLetter.data = Object.assign(newLetter.data || {}, { template: 'no-issues' });
      }
      const existingLetters = await this.flagManagerService.loadLetters(this.inspection.id);
      this.flagManagerService.setLetters(existingLetters);
      if (!this.inspection.hash) {
        this.inspection = await this.inspectionService.get(this.inspection.id);
        this.refreshInspection.emit(this.inspection);
      }
      console.log('Letter created successfully:', newLetter);
    } catch (error) {
      console.error('Error creating letter:', error);
      this.toastr.error('Failed to create letter');
    }
  }

  setCustomerUrl() {
    this.customerUrl = location.origin + '/recommendation-letter/' + encodeURIComponent(this.inspection.hash);
  }

  async handlePreviewLetter(event: any): Promise<void> {
    const { userId, previewVersionFlag } = event;
    try {
      let previewLetter;
      if (previewVersionFlag) {
        previewLetter = await this.inspectionService.generateLetter(this.inspection.id, userId, true);
      } else {
        previewLetter = await this.inspectionService.generateNoIssuesLetter(this.inspection.id, userId, true);
      }
      this.toastr.success('Your Preview letter is being generated');
      if (!this.inspection.hash) {
        this.inspection = await this.inspectionService.get(this.inspection.id);
        this.refreshInspection.emit(this.inspection);
      }
      this.setCustomerUrl();

      window.open(`${this.customerUrl}?version=${previewLetter.id}`, 'Recommendation Letter');
    } catch (error) {
      console.error('Error previewing letter:', error);
      this.toastr.error('Failed to preview letter');
    }
  }

  openLetterHistoryPanel(): void {
    this.isDropdownOpen = false;
    this.openLetterHistory.emit();
  }

  toggleJournalPanel(): void {
    this.showJournalPanel = !this.showJournalPanel;
  }

  ngOnDestroy(): void {}
}
